import api from '@/lib/api';
import type {
  CreatePreboardingFormFieldInput,
  CreatePreboardingFormSectionInput,
  PreboardingFormField,
  PreboardingFormSection,
  ReorderPreboardingFormFieldsInput,
  ReorderPreboardingFormSectionsInput,
  UpdatePreboardingFormFieldInput,
  UpdatePreboardingFormSectionInput,
} from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const preboardingFormBuilderKeys = {
  all: ['preboarding-form-builder'] as const,
  sections: (formId: string) =>
    [...preboardingFormBuilderKeys.all, formId, 'sections'] as const,
};

async function listSections(formId: string): Promise<PreboardingFormSection[]> {
  const { data } = await api.get<PreboardingFormSection[]>(
    `/preboarding-forms/${formId}/sections`,
  );
  return data;
}

async function createSection(
  formId: string,
  input: CreatePreboardingFormSectionInput,
): Promise<PreboardingFormSection> {
  const { data } = await api.post<PreboardingFormSection>(
    `/preboarding-forms/${formId}/sections`,
    input,
  );
  return data;
}

async function updateSection(
  formId: string,
  sectionId: string,
  input: UpdatePreboardingFormSectionInput,
): Promise<PreboardingFormSection> {
  const { data } = await api.patch<PreboardingFormSection>(
    `/preboarding-forms/${formId}/sections/${sectionId}`,
    input,
  );
  return data;
}

async function deleteSection(formId: string, sectionId: string): Promise<void> {
  await api.delete(`/preboarding-forms/${formId}/sections/${sectionId}`);
}

async function reorderSections(
  formId: string,
  input: ReorderPreboardingFormSectionsInput,
): Promise<PreboardingFormSection[]> {
  const { data } = await api.post<PreboardingFormSection[]>(
    `/preboarding-forms/${formId}/sections/reorder`,
    input,
  );
  return data;
}

async function createField(
  formId: string,
  sectionId: string,
  input: CreatePreboardingFormFieldInput,
): Promise<PreboardingFormField> {
  const { data } = await api.post<PreboardingFormField>(
    `/preboarding-forms/${formId}/sections/${sectionId}/fields`,
    input,
  );
  return data;
}

async function updateField(
  formId: string,
  sectionId: string,
  fieldId: string,
  input: UpdatePreboardingFormFieldInput,
): Promise<PreboardingFormField> {
  const { data } = await api.patch<PreboardingFormField>(
    `/preboarding-forms/${formId}/sections/${sectionId}/fields/${fieldId}`,
    input,
  );
  return data;
}

async function deleteField(
  formId: string,
  sectionId: string,
  fieldId: string,
): Promise<void> {
  await api.delete(
    `/preboarding-forms/${formId}/sections/${sectionId}/fields/${fieldId}`,
  );
}

async function reorderFields(
  formId: string,
  sectionId: string,
  input: ReorderPreboardingFormFieldsInput,
): Promise<PreboardingFormField[]> {
  const { data } = await api.post<PreboardingFormField[]>(
    `/preboarding-forms/${formId}/sections/${sectionId}/fields/reorder`,
    input,
  );
  return data;
}

export function usePreboardingFormSections(formId: string) {
  return useQuery({
    queryKey: preboardingFormBuilderKeys.sections(formId),
    queryFn: () => listSections(formId),
    enabled: !!formId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

// All builder mutations invalidate the form's section tree on settle. Reorders
// additionally apply an optimistic reorder so drag-and-drop feels instant.
function useInvalidateSections(formId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: preboardingFormBuilderKeys.sections(formId),
    });
}

export function useCreatePreboardingSection(formId: string) {
  const invalidate = useInvalidateSections(formId);
  return useMutation({
    mutationFn: (input: CreatePreboardingFormSectionInput) =>
      createSection(formId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: invalidate,
  });
}

export function useUpdatePreboardingSection(formId: string) {
  const invalidate = useInvalidateSections(formId);
  return useMutation({
    mutationFn: ({
      sectionId,
      ...input
    }: UpdatePreboardingFormSectionInput & { sectionId: string }) =>
      updateSection(formId, sectionId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: invalidate,
  });
}

export function useDeletePreboardingSection(formId: string) {
  const invalidate = useInvalidateSections(formId);
  return useMutation({
    mutationFn: (sectionId: string) => deleteSection(formId, sectionId),
    meta: { suppressGlobalToast: true },
    onSuccess: invalidate,
  });
}

export function useReorderPreboardingSections(formId: string) {
  const queryClient = useQueryClient();
  const key = preboardingFormBuilderKeys.sections(formId);
  return useMutation({
    mutationFn: (input: ReorderPreboardingFormSectionsInput) =>
      reorderSections(formId, input),
    // Rollback-only onError; call sites supply their own toast. Explicit flag for consistency.
    meta: { suppressGlobalToast: true },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PreboardingFormSection[]>(key);
      if (previous) {
        const lookup = new Map(previous.map((s) => [s.id, s]));
        const optimistic = input.sectionIds
          .map((id, index) => {
            const section = lookup.get(id);
            return section ? { ...section, position: index } : null;
          })
          .filter((s): s is PreboardingFormSection => s !== null);
        queryClient.setQueryData(key, optimistic);
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

export function useCreatePreboardingField(formId: string) {
  const invalidate = useInvalidateSections(formId);
  return useMutation({
    mutationFn: ({
      sectionId,
      ...input
    }: CreatePreboardingFormFieldInput & { sectionId: string }) =>
      createField(formId, sectionId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: invalidate,
  });
}

export function useUpdatePreboardingField(formId: string) {
  const invalidate = useInvalidateSections(formId);
  return useMutation({
    mutationFn: ({
      sectionId,
      fieldId,
      ...input
    }: UpdatePreboardingFormFieldInput & { sectionId: string; fieldId: string }) =>
      updateField(formId, sectionId, fieldId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: invalidate,
  });
}

export function useDeletePreboardingField(formId: string) {
  const invalidate = useInvalidateSections(formId);
  return useMutation({
    mutationFn: ({ sectionId, fieldId }: { sectionId: string; fieldId: string }) =>
      deleteField(formId, sectionId, fieldId),
    meta: { suppressGlobalToast: true },
    onSuccess: invalidate,
  });
}

export function useReorderPreboardingFields(formId: string) {
  const queryClient = useQueryClient();
  const key = preboardingFormBuilderKeys.sections(formId);
  return useMutation({
    mutationFn: ({
      sectionId,
      ...input
    }: ReorderPreboardingFormFieldsInput & { sectionId: string }) =>
      reorderFields(formId, sectionId, input),
    // Rollback-only onError; call sites supply their own toast. Explicit flag for consistency.
    meta: { suppressGlobalToast: true },
    onMutate: async ({ sectionId, fieldIds }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PreboardingFormSection[]>(key);
      if (previous) {
        const optimistic = previous.map((section) => {
          if (section.id !== sectionId) return section;
          const lookup = new Map(section.fields.map((f) => [f.id, f]));
          const fields = fieldIds
            .map((id, index) => {
              const field = lookup.get(id);
              return field ? { ...field, position: index } : null;
            })
            .filter((f): f is PreboardingFormField => f !== null);
          return { ...section, fields };
        });
        queryClient.setQueryData(key, optimistic);
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}
